import React, { useState, useMemo, useEffect } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, TablePagination, Paper, IconButton,
  TextField, InputAdornment, Tooltip, Chip, Typography, Stack, Checkbox, Button, Alert
} from '@mui/material';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import InboxIcon from '@mui/icons-material/Inbox';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import BusinessIcon from '@mui/icons-material/Business';
import Collapse from '@mui/material/Collapse';
import { TABLE_CONFIG } from '../config/fieldConfig';
import { fetchRecords } from '../services/api';
import CompanyStatutoryPanel from './CompanyStatutoryPanel';
import PersonOnboardingPanel from './PersonOnboardingPanel';
import { useApp } from '../context/AppContext';
import ConfirmDialog from './ConfirmDialog';

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

// ── Smart cell helpers (Option 4 — Phase 1 polish) ─────────────────────────────
// Columns auto-hidden by default — they're rarely useful for understanding a row.
// Toggle "Show all columns" in the toolbar to bring them back.
const REDUNDANT_COLS = new Set([
  'COMPANY_id', 'bg_id', 'ldg_id', 'ldg_code',
  'created_by', 'updated_by', 'created_at', 'updated_at',
  'effective_to',
]);

// Pattern-match amount-like columns to render with ₹ + colour + sign
const AMOUNT_PATTERNS = [
  /amount$/i, /^amount/i, /_amount$/i, /^total_/i, /^net_/i, /^gross_/i,
  /emi_amount/i, /loan_amount/i, /^balance/i, /^outstanding/i, /^recovered/i,
  /salary$/i, /^salary_/i, /_ctc$/i, /payable$/i, /earnings$/i, /deductions$/i,
  /^principal/i, /^interest/i, /_tax$/i, /^tds/i, /^ytd_/i,
  /^monthly_/i, /_monthly$/i,
];
const isAmountCol = (col) => AMOUNT_PATTERNS.some(rx => rx.test(col));

// Format a number as Indian rupee with + / - sign
const formatINR = (num) => {
  const n = Number(num);
  if (!Number.isFinite(n)) return null;
  const sign = n > 0 ? '+' : n < 0 ? '−' : '';
  const abs  = Math.abs(n).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  return { display: `${sign}₹${abs}`, color: n > 0 ? '#16a34a' : n < 0 ? '#dc2626' : '#6B7280' };
};

// Classification chip style mapping
const CLASSIFICATION_CHIPS = {
  'Earning':                { bg: '#dcfce7', fg: '#15803d', border: '#bbf7d0', icon: '💰' },
  'Deduction':              { bg: '#fef2f2', fg: '#991b1b', border: '#fecaca', icon: '🧾' },
  'Employer Contribution':  { bg: '#dbeafe', fg: '#1e40af', border: '#bfdbfe', icon: '🏢' },
  'Tax':                    { bg: '#fef3c7', fg: '#92400e', border: '#fde68a', icon: '⚖️' },
};

// Format a date string YYYY-MM-DD → DD-MMM-YYYY (e.g. 01-Feb-2026)
const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const formatDate = (str) => {
  if (typeof str !== 'string') return null;
  const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const [, y, mo, d] = m;
  const monthIdx = parseInt(mo, 10) - 1;
  if (monthIdx < 0 || monthIdx > 11) return null;
  return `${d}-${MONTH_SHORT[monthIdx]}-${y}`;
};

// Generic status chip palette (for *_status columns)
const STATUS_CHIPS = {
  active:    { bg: '#dcfce7', fg: '#16a34a', border: '#bbf7d0' },
  inactive:  { bg: '#F3F4F6', fg: '#6B7280', border: '#E5E7EB' },
  pending:   { bg: '#fef3c7', fg: '#92400e', border: '#fde68a' },
  approved:  { bg: '#dcfce7', fg: '#15803d', border: '#bbf7d0' },
  rejected:  { bg: '#fef2f2', fg: '#991b1b', border: '#fecaca' },
  completed: { bg: '#dbeafe', fg: '#1e40af', border: '#bfdbfe' },
  paid:      { bg: '#dcfce7', fg: '#15803d', border: '#bbf7d0' },
  failed:    { bg: '#fef2f2', fg: '#991b1b', border: '#fecaca' },
  draft:     { bg: '#F3F4F6', fg: '#6B7280', border: '#E5E7EB' },
  eligible:      { bg: '#dcfce7', fg: '#15803d', border: '#bbf7d0' },
  not_eligible:  { bg: '#fef2f2', fg: '#991b1b', border: '#fecaca' },
  conditional:   { bg: '#fef3c7', fg: '#92400e', border: '#fde68a' },
};

export default function DataTable({ records, schema, onEdit, onDelete, onView, tableName, onManagePayroll, activeCompanyId, customRowAction, canCreate = true }) {
  const [search,      setSearch]      = useState('');
  const [orderBy,     setOrderBy]     = useState('');
  const [order,       setOrder]       = useState('asc');
  const [page,        setPage]        = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [joinedData, setJoinedData] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [showAllCols, setShowAllCols] = useState(false);

  const [selectedIds, setSelectedIds] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const { removeRecord, reloadRecords, state } = useApp();
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleBulkDelete = async () => {
    setIsDeleting(true);
    try {
      await Promise.all(selectedIds.map(id => removeRecord(tableName, id)));
      enqueueSnackbar(`Successfully deleted ${selectedIds.length} records`, { variant: 'success' });
      setSelectedIds([]);
      if (typeof reloadRecords === 'function') {
        reloadRecords(tableName);
      } else {
        window.location.reload();
      }
    } catch (err) {
      enqueueSnackbar('Bulk delete failed: ' + (err.userMessage || err.message), { variant: 'error' });
    } finally {
      setIsDeleting(false);
      setBulkDeleteDialogOpen(false);
    }
  };
  
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dateError, setDateError] = useState('');

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (fromDate && toDate && fromDate > toDate) {
      setDateError('Invalid date range');
    } else {
      setDateError('');
    }
  }, [fromDate, toDate]);

  const isCompanyTable = tableName === 'COMPANY';
  const isPersonTable  = tableName === 'PERSON';
  const isExpandable   = isCompanyTable || isPersonTable;
  const expandTooltip  = isCompanyTable ? 'Show statutory'
                        : isPersonTable ? 'Show onboarding checklist'
                        : 'Show details';

  // Determine PK to hide: use TABLE_CONFIG if available, else detect first *_id field
  const tableConfig   = tableName ? TABLE_CONFIG[tableName] : null;

  // Auto-derive displayJoins from any 'dropdown' or 'multi-dropdown' field config.
  // Each such field has a source / valueField / labelField — that's enough to
  // render "Name <newline> ID" two-line cells without per-table explicit setup.
  // Explicit `displayJoins` on tableConfig overrides auto-derived entries.
  const displayJoins = useMemo(() => {
    const auto = {};
    Object.entries(tableConfig?.fields || {}).forEach(([f, cfg]) => {
      const t = cfg?.type;
      if ((t !== 'dropdown' && t !== 'multi-dropdown') || !cfg.source || !cfg.valueField) return;
      const fallbackCombo =
        cfg.labelField && cfg.labelField !== cfg.valueField
          ? [cfg.valueField, cfg.labelField]
          : undefined;
      auto[f] = {
        source:     cfg.source,
        valueField: cfg.valueField,
        labelField: cfg.labelField,
        labelCombo: cfg.labelCombo || fallbackCombo,
      };
    });
    return { ...auto, ...(tableConfig?.displayJoins || {}) };
  }, [tableConfig]);
  const pkField = tableConfig?.pk
    || schema.find(c => c !== '_id' && c.endsWith('_id'))
    || schema[0];

  const pendingUpdateRecord = useMemo(() => {
    if (tableName !== 'PF_ACCOUNT' && tableName !== 'ESI_ACCOUNT') return null;
    const today = new Date().toISOString().split('T')[0];
    return records.find(r => r.pending_update_date && r.pending_update_date > today);
  }, [tableName, records]);

  const showPk      = tableConfig?.showPk === true;
  const allColumns  = schema.filter(c => c !== '_id' && (showPk || c !== pkField));

  // Phase 1 polish — auto-hide redundant org/audit columns unless user opted in.
  // For tables WITHOUT explicit displayColumns, also cap to first 8 visible.
  const filterRedundant = (cols) => showAllCols ? cols : cols.filter(c => !REDUNDANT_COLS.has(c));
  const visibleCols = tableConfig?.displayColumns
    ? filterRedundant(tableConfig.displayColumns.filter(c => allColumns.includes(c) || (showPk && c === pkField)))
    : filterRedundant(allColumns).slice(0, 8);
  const hiddenCount = allColumns.length - visibleCols.length;

  // ── Fetch FK tables needed for display joins (including chain sources) ─
  useEffect(() => {
    const sources = new Set();
    Object.values(displayJoins).forEach(j => {
      if (j.source) sources.add(j.source);
      if (j.chain?.source) sources.add(j.chain.source);
    });
    if (sources.size === 0) return;
    Array.from(sources).forEach(source => {
      fetchRecords(source)
        .then(res => setJoinedData(prev => ({ ...prev, [source]: res.data.data || [] })))
        .catch(() => {});
    });
  }, [tableName, displayJoins]);

  // ── Resolve a FK cell value to structured display info ────────────────
  // Returns { primary, secondary, id } when a match is found, else null.
  // If a `chain` is configured (e.g. assignment_id → ASSIGNMENT.person_id → PERSON.full_name),
  // the chained value becomes `primary` and the local label becomes `secondary`.
  const resolveJoin = (value, col) => {
    const join = displayJoins[col];
    if (!join || !value) return null;
    const rows  = joinedData[join.source] || [];
    const match = rows.find(r => r[join.valueField] === value);
    if (!match) return null;

    const id = match[join.valueField];

    // Local label: labelCombo (excluding id itself) or labelField
    let localName = null;
    if (Array.isArray(join.labelCombo)) {
      const parts = join.labelCombo.map(f => match[f]).filter(Boolean);
      const nonIdParts = parts.filter(p => p !== id);
      localName = nonIdParts.join(' - ') || null;
    } else if (join.labelField && match[join.labelField] && match[join.labelField] !== id) {
      localName = match[join.labelField];
    }

    // Chained lookup: one extra FK hop (e.g. ASSIGNMENT → PERSON via person_id)
    let chainName = null;
    if (join.chain && match[join.chain.via]) {
      const chainRows  = joinedData[join.chain.source] || [];
      const chainMatch = chainRows.find(r => r[join.chain.keyField] === match[join.chain.via]);
      if (chainMatch && join.chain.labelField) {
        chainName = chainMatch[join.chain.labelField] || null;
      }
    }

    return {
      primary:   chainName || localName,
      secondary: chainName ? localName : null,
      id,
    };
  };

  const filtered = useMemo(() => {
    let result = records;

    if (tableName === 'ATTENDANCE' && !dateError) {
      if (fromDate) {
        result = result.filter(r => {
          if (!r.attendance_date) return false;
          return r.attendance_date.substring(0, 10) >= fromDate;
        });
      }
      if (toDate) {
        result = result.filter(r => {
          if (!r.attendance_date) return false;
          return r.attendance_date.substring(0, 10) <= toDate;
        });
      }
    }

    if (!search.trim()) return result;
    const q = search.toLowerCase();
    return result.filter(r => {
      if (Object.values(r).some(v => v !== null && v !== undefined && String(v).toLowerCase().includes(q))) return true;
      // Also search resolved join display values
      return Object.keys(displayJoins).some(col => {
        const resolved = resolveJoin(r[col], col);
        if (!resolved) return false;
        const text = [resolved.primary, resolved.secondary, resolved.id].filter(Boolean).join(' ');
        return text.toLowerCase().includes(q);
      });
    });
  }, [records, search, joinedData, displayJoins, tableName, fromDate, toDate, dateError]);

  const sorted = useMemo(() => {
    if (!orderBy) return filtered;
    return [...filtered].sort((a, b) => {
      const cmp = String(a[orderBy] ?? '').localeCompare(String(b[orderBy] ?? ''), undefined, { numeric: true });
      return order === 'asc' ? cmp : -cmp;
    });
  }, [filtered, orderBy, order]);

  const paginated = useMemo(() =>
    sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sorted, page, rowsPerPage]
  );

  const handleSort = (col) => {
    if (orderBy === col) setOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setOrderBy(col); setOrder('asc'); }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(filtered.map(r => r.id ?? r._id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleExport = async () => {
    if (selectedIds.length === 0) {
      enqueueSnackbar('Please select at least one record to export.', { variant: 'warning' });
      return;
    }
    enqueueSnackbar('Downloading file...', { variant: 'info' });

    let exportData = [];
    let sheetName = "Export";
    let fileName = `Export_${new Date().toISOString().split('T')[0]}.xlsx`;

    if (tableName === 'ELEMENT_TYPE') {
      const selectedRecords = filtered.filter(r => selectedIds.includes(r.id ?? r._id));
      exportData = selectedRecords.map(r => ({
        element_name: r.element_name,
        element_code: r.element_code,
        type: r.type,
        calculation_rule: r.calculation_rule,
        flat_amount: r.flat_amount,
        percentage_value: r.percentage_value,
        base_element: r.base_element,
        formula_reference: r.formula?.formula_expression || r.formula_reference,
        processing_priority: r.processing_priority,
        taxable_flag: r.taxable_flag ? 'Y' : 'N',
        pf_applicable: r.pf_applicable ? 'Y' : 'N',
        esi_applicable: r.esi_applicable ? 'Y' : 'N',
        show_in_payslip: r.show_in_payslip ? 'Y' : 'N',
        pro_rata_flag: r.pro_rata_flag ? 'Y' : 'N',
      }));
      sheetName = "Salary Structure (Components)";
      fileName = `Salary_Structure_Components_${new Date().toISOString().split('T')[0]}.xlsx`;
    } else if (tableName === 'SALARY_TEMPLATE') {
      let token = '';
      try {
        const auth = JSON.parse(localStorage.getItem('payroll.auth') || '{}');
        token = auth.token || '';
      } catch (e) {}

      try {
        const response = await fetch('/api/salary-templates/bulk-export', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ template_ids: selectedIds })
        });
        const result = await response.json();
        if (result.success) {
          exportData = result.data;
          sheetName = "Salary Templates";
          fileName = `Salary_Templates_${new Date().toISOString().split('T')[0]}.xlsx`;
        } else {
          enqueueSnackbar(result.message || 'Export failed', { variant: 'error' });
          return;
        }
      } catch (err) {
        enqueueSnackbar('Failed to fetch export data: ' + err.message, { variant: 'error' });
        return;
      }
    }

    if (exportData.length === 0) {
       enqueueSnackbar('No data to export', { variant: 'warning' });
       return;
    }

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, fileName);
  };

  const fileInputRef = React.useRef(null);
  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data);
      const ws = wb.Sheets[wb.SheetNames[0]];
      const records = XLSX.utils.sheet_to_json(ws);
      
      let token = '';
      try {
        const auth = JSON.parse(localStorage.getItem('payroll.auth') || '{}');
        token = auth.token || '';
      } catch (e) {}

      if (!activeCompanyId) {
        enqueueSnackbar('Please select an Active Company first before importing', { variant: 'warning' });
        return;
      }

      const bgId = state?.activeContext?.bg_id || '';
      const lgId = state?.activeContext?.ldg_id || '';

      let endpoint = '';
      if (tableName === 'ELEMENT_TYPE') {
        endpoint = `/api/element-type/bulk-import?active_company_id=${activeCompanyId}&active_bg_id=${bgId}&active_ldg_id=${lgId}`;
      } else if (tableName === 'SALARY_TEMPLATE') {
        endpoint = `/api/salary-templates/bulk-import?active_company_id=${activeCompanyId}&active_bg_id=${bgId}&active_ldg_id=${lgId}`;
      } else {
        return;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ records })
      });
      const result = await response.json();
      if (result.success) {
        enqueueSnackbar(`Import complete: ${result.successCount} imported, ${result.skippedCount} skipped, ${result.errors?.length || 0} failed.`, { variant: 'success' });
        if (result.errors?.length > 0) {
           console.warn('Import errors:', result.errors);
           enqueueSnackbar('Check console for specific row errors', { variant: 'warning' });
        }
        window.location.reload(); // Quick refresh to see new records
      } else {
        enqueueSnackbar(result.message || 'Import failed', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to process file: ' + err.message, { variant: 'error' });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };



  const formatCell = (value, col, row) => {
    if (value === null || value === undefined || value === '')
      return <Typography fontSize={12.5} sx={{ color: '#D1D5DB' }}>—</Typography>;
    const str = String(value);  
    const fieldCfg = tableConfig?.fields?.[col];

    // ── (0) Custom PAYROLL payment_day override ───────────────────────────
    if (tableName === 'PAYROLL' && col === 'payment_day') {
      const pType = String(row.period_type || '').toLowerCase();
      if (pType === 'weekly' || pType === 'biweekly' || pType === 'bi_weekly') {
        const offset = row.payment_date_offset || 0;
        return (
          <Tooltip title={`Weekly/Bi-weekly: Pay ${offset} days after cycle end`} arrow>
            <Typography fontSize={13} color="#374151">+ {offset} days</Typography>
          </Tooltip>
        );
      }
    }

    // ── (1) Amount-like columns → ₹ formatted with colour + sign ──────────
    if (isAmountCol(col)) {
      const fmt = formatINR(value);
      if (fmt) {
        const isPositive = Number(value) > 0;
        const isNegative = Number(value) < 0;
        return (
          <Stack direction="row" alignItems="center" spacing={0.5} justifyContent="flex-end">
            {isPositive && <TrendingUpIcon sx={{ fontSize: 13, color: fmt.color }} />}
            {isNegative && <TrendingDownIcon sx={{ fontSize: 13, color: fmt.color }} />}
            <Typography fontSize={13} fontWeight={600} sx={{
              color: fmt.color, fontFamily: 'monospace',
            }}>
              {fmt.display}
            </Typography>
          </Stack>
        );
      }
    }

    // ── (2) Classification column → coloured chip with icon ───────────────
    if (col === 'classification' && CLASSIFICATION_CHIPS[str]) {
      const c = CLASSIFICATION_CHIPS[str];
      return (
        <Chip
          label={`${c.icon} ${str}`}
          size="small"
          sx={{
            height: 22, fontSize: 11, fontWeight: 600, borderRadius: '20px',
            bgcolor: c.bg, color: c.fg, border: `1px solid ${c.border}`,
            '& .MuiChip-label': { px: 1 },
          }}
        />
      );
    }

    // ── (3) Status columns → coloured status chip ─────────────────────────
    if (/_status$|^status$|^.*flag$/.test(col) && col !== 'active_flag') {
      const key = str.toLowerCase().replace(/[\s-]/g, '_');
      const style = STATUS_CHIPS[key] || STATUS_CHIPS[str.toLowerCase().split('_')[0]];
      if (style) {
        return (
          <Chip label={str} size="small" sx={{
            height: 22, fontSize: 11, fontWeight: 600, borderRadius: '20px',
            bgcolor: style.bg, color: style.fg, border: `1px solid ${style.border}`,
          }} />
        );
      }
    }

    // ── (4) Date columns → DD-MMM-YYYY pretty format ──────────────────────
    if (fieldCfg?.type === 'date' || /(_date|^date_|_at$|_on$)/.test(col)) {
      const pretty = formatDate(str);
      if (pretty) {
        return (
          <Tooltip title={str} arrow>
            <Typography fontSize={12.5} sx={{ color: '#374151', fontFamily: 'monospace', letterSpacing: 0.2 }}>
              {pretty}
            </Typography>
          </Tooltip>
        );
      }
    }

    // ── Display join: 1–3 line cell depending on what's resolved ─────────
    const resolved = resolveJoin(value, col);
    if (resolved !== null) {
      const { primary, secondary, id } = resolved;
      return (
        <Stack spacing={0.1}>
          <Typography fontSize={13} fontWeight={500} color="#374151" lineHeight={1.3}>
            {primary || id}
          </Typography>
          {secondary && (
            <Typography fontSize={11} color="#6B7280" lineHeight={1.2}>
              {secondary}
            </Typography>
          )}
          {primary && primary !== id && (
            <Typography fontSize={10.5} color="#9CA3AF" sx={{ fontFamily: 'monospace', letterSpacing: 0.2 }}>
              {id}
            </Typography>
          )}
        </Stack>
      );
    }

    if (tableConfig?.fields?.[col]?.type === 'auto-code') return (
      <Chip label={str} size="small" sx={{
        fontFamily: 'monospace', fontWeight: 700, fontSize: 11,
        bgcolor: '#dcfce7', color: '#14532d',
        border: '1px solid #bbf7d0', height: 22, letterSpacing: 0.8,
      }} />
    );
    if (str === 'Y') return (
      <Chip label="Active" size="small" sx={{
        height: 22, fontSize: 11, fontWeight: 600, borderRadius: '20px',
        bgcolor: '#dcfce7', color: '#16a34a', border: '1px solid #bbf7d0',
      }} />
    );
    if (str === 'N') return (
      <Chip label="Inactive" size="small" sx={{
        height: 22, fontSize: 11, fontWeight: 600, borderRadius: '20px',
        bgcolor: '#F3F4F6', color: '#6B7280', border: '1px solid #E5E7EB',
      }} />
    );
    if (str.length > 32) return (
      <Tooltip title={str} arrow>
        <Typography fontSize={13} sx={{ cursor: 'default', color: '#374151' }}>
          {str.slice(0, 30)}…
        </Typography>
      </Tooltip>
    );
    return <Typography fontSize={13} color="#374151">{str}</Typography>;
  };

  const colLabel = (col) =>
    col.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const startRow = page * rowsPerPage + 1;
  const endRow   = Math.min(page * rowsPerPage + rowsPerPage, filtered.length);

  return (
    <Box>
      {/* ── Toolbar ── */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        mb: 1.5, gap: 2, flexWrap: 'wrap',
      }}>
        {/* Search & Filters */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', flex: 1 }}>
          <TextField
            size="small"
            placeholder="Search records…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            sx={{
              width: { xs: '100%', sm: 300 },
              '& .MuiOutlinedInput-root': {
                bgcolor: 'white', fontSize: 13, borderRadius: '10px',
                '& fieldset': { borderColor: '#E8EDF5', borderWidth: '1.5px' },
                '&:hover fieldset': { borderColor: '#bbf7d0' },
                '&.Mui-focused fieldset': { borderColor: '#22c55e', borderWidth: '2px' },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#94A3B8', fontSize: 17 }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => { setSearch(''); setPage(0); }}
                    sx={{ color: '#94A3B8', '&:hover': { color: '#22c55e' } }}>
                    <ClearIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {(tableName === 'PF_ACCOUNT' || tableName === 'ESI_ACCOUNT') && pendingUpdateRecord && (
            <Alert
              severity="warning"
              sx={{
                borderRadius: '10px', py: 0, '& .MuiAlert-message': { py: 0.5, fontSize: 13, fontWeight: 500 },
                display: 'flex', alignItems: 'center'
              }}
            >
              Assignment ID: {pendingUpdateRecord.assignment_id} &ndash; {tableName === 'PF_ACCOUNT' ? 'PF' : 'ESI'} contribution and related values will be updated from the Effective Date: {pendingUpdateRecord.pending_update_date.split('-').reverse().join('-')}.
            </Alert>
          )}

          {tableName === 'ATTENDANCE' && (
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                type="date"
                label="From Date"
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: today }}
                value={fromDate}
                onChange={e => { setFromDate(e.target.value); setPage(0); }}
                sx={{ width: 140, bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
              <TextField
                type="date"
                label="To Date"
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: fromDate || '', max: today }}
                value={toDate}
                onChange={e => { setToDate(e.target.value); setPage(0); }}
                error={!!dateError}
                helperText={dateError}
                FormHelperTextProps={{ sx: { position: 'absolute', bottom: -20, ml: 0 } }}
                sx={{ width: 140, bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: '10px' }, '& .MuiFormHelperText-root': { fontSize: 10 } }}
              />
            </Stack>
          )}
        </Box>

        {/* Right side info */}
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {search && (
            <Chip
              label={`${filtered.length} result${filtered.length !== 1 ? 's' : ''}`}
              size="small"
              sx={{
                bgcolor: '#f0fdf4', color: '#16a34a', fontWeight: 700,
                fontSize: 11, height: 24, borderRadius: '8px',
              }}
            />
          )}

          {/* Show all columns toggle — appears only when there are hidden columns */}
          {hiddenCount > 0 && (
            <Tooltip
              title={showAllCols
                ? `Hide ${REDUNDANT_COLS.size} system columns (Company/BG/LDG/audit)`
                : `Show ${hiddenCount} more column${hiddenCount !== 1 ? 's' : ''} (Company/BG/LDG/audit)`}
              arrow
            >
              <Chip
                icon={showAllCols
                  ? <VisibilityOffIcon sx={{ fontSize: '14px !important' }} />
                  : <VisibilityIcon sx={{ fontSize: '14px !important' }} />}
                label={showAllCols ? 'Hide system cols' : `+${hiddenCount} more cols`}
                size="small"
                onClick={() => setShowAllCols(s => !s)}
                clickable
                sx={{
                  bgcolor: showAllCols ? '#dbeafe' : 'white',
                  color:   showAllCols ? '#1e40af' : '#6B7280',
                  fontWeight: 600, fontSize: 11, height: 24, borderRadius: '8px',
                  border: '1.5px solid',
                  borderColor: showAllCols ? '#bfdbfe' : '#E8EDF5',
                  '&:hover': { borderColor: '#2563eb', color: '#1e40af' },
                }}
              />
            </Tooltip>
          )}
          <Stack direction="row" alignItems="center" gap={0.6}
            sx={{ px: 1.5, py: 0.5, borderRadius: '8px', bgcolor: 'white',
              border: '1.5px solid #E8EDF5' }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#10B981' }} />
            <Typography fontSize={12} fontWeight={600} color="text.secondary">
              {records.length} records
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" gap={0.6}
            sx={{ px: 1.5, py: 0.5, borderRadius: '8px', bgcolor: 'white',
              border: '1.5px solid #E8EDF5' }}>
            <Typography fontSize={12} fontWeight={600} color="text.secondary">
              {visibleCols.length} cols
            </Typography>
          </Stack>
        </Stack>

        {/* Toolbar actions for Export/Import */}
        {['ELEMENT_TYPE', 'SALARY_TEMPLATE'].includes(tableName) && (
          <Stack direction="row" alignItems="center" gap={1}>
            {selectedIds.length > 0 && (
              <Chip 
                label={`${selectedIds.length} selected`} 
                size="small" 
                sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontWeight: 600, fontSize: 11 }}
              />
            )}
            {selectedIds.length > 0 && (
              <Button 
                size="small" 
                variant="outlined" 
                color="error"
                onClick={() => setBulkDeleteDialogOpen(true)}
                disabled={isDeleting}
                sx={{ borderColor: '#fecaca', textTransform: 'none', height: 28, '&:hover': { borderColor: '#ef4444', bgcolor: '#fef2f2' } }}
              >
                Delete
              </Button>
            )}
            <Button 
              size="small" 
              variant="outlined" 
              onClick={handleExport}
              sx={{ borderColor: '#E8EDF5', color: '#374151', textTransform: 'none', height: 28, '&:hover': { borderColor: '#16a34a', color: '#16a34a', bgcolor: '#f0fdf4' } }}
            >
              Export
            </Button>
            <Button 
              size="small" 
              variant="outlined" 
              component="label"
              sx={{ borderColor: '#E8EDF5', color: '#374151', textTransform: 'none', height: 28, '&:hover': { borderColor: '#2563eb', color: '#2563eb', bgcolor: '#eff6ff' } }}
            >
              Import
              <input type="file" accept=".xlsx, .xls" hidden ref={fileInputRef} onChange={handleImport} />
            </Button>
          </Stack>
        )}
      </Box>

      {/* ── Table card ── */}
      <Paper sx={{
        overflow: 'hidden',
        border: '1.5px solid #E5E7EB',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        borderRadius: '12px',
      }}>
        <TableContainer sx={{ maxHeight: 560 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {/* Checkbox column (if ELEMENT_TYPE or SALARY_TEMPLATE) */}
                {['ELEMENT_TYPE', 'SALARY_TEMPLATE'].includes(tableName) && (
                  <TableCell padding="checkbox" sx={{ bgcolor: '#F9FAFB', borderBottom: '1.5px solid #E5E7EB' }}>
                    <Checkbox
                      color="primary"
                      checked={filtered.length > 0 && selectedIds.length === filtered.length}
                      indeterminate={selectedIds.length > 0 && selectedIds.length < filtered.length}
                      onChange={handleSelectAll}
                      size="small"
                    />
                  </TableCell>
                )}

                {/* SL */}
                <TableCell sx={{
                  width: 52, py: 1.5, textAlign: 'center',
                  bgcolor: '#F9FAFB', borderBottom: '1.5px solid #E5E7EB',
                  color: '#9CA3AF', fontSize: 11, fontWeight: 600, letterSpacing: 0.4,
                }}>SL</TableCell>

                {visibleCols.map(col => (
                  <TableCell key={col} align={['sort_order', 'esi_ip_number', 'esi_estab_code'].includes(col) ? 'center' : 'left'} sx={{
                    py: 1.5, minWidth: 120, whiteSpace: 'nowrap',
                    bgcolor: '#F9FAFB', borderBottom: '1.5px solid #E5E7EB',
                  }}>
                    <TableSortLabel
                      active={orderBy === col}
                      direction={orderBy === col ? order : 'asc'}
                      onClick={() => handleSort(col)}
                      sx={{
                        color: '#6B7280 !important',
                        fontSize: 12.5, fontWeight: 500, letterSpacing: 0.2,
                        '& .MuiTableSortLabel-icon': { color: '#D1D5DB !important', fontSize: 14 },
                        '&.Mui-active': { color: '#22c55e !important' },
                        '&:hover': { color: '#374151 !important' },
                      }}
                    >
                      {colLabel(col)}
                    </TableSortLabel>
                  </TableCell>
                ))}

                <TableCell align="center" sx={{
                  width: isCompanyTable ? 280 : isPersonTable ? 140 : 100, py: 1.5,
                  bgcolor: '#F9FAFB', borderBottom: '1.5px solid #E5E7EB',
                  color: '#9CA3AF', fontSize: 11, fontWeight: 600, letterSpacing: 0.4,
                }}>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleCols.length + (['ELEMENT_TYPE', 'SALARY_TEMPLATE'].includes(tableName) ? 3 : 2)}
                    sx={{ py: 10, textAlign: 'center', border: 'none', bgcolor: 'white' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{
                        width: 56, height: 56, borderRadius: '14px',
                        bgcolor: '#F3F4F6',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <InboxIcon sx={{ fontSize: 26, color: '#9CA3AF' }} />
                      </Box>
                      <Typography fontWeight={600} fontSize={14} color="#374151">
                        {search ? 'No matching records' : 'No records yet'}
                      </Typography>
                      <Typography fontSize={12.5} color="#9CA3AF">
                        {search ? 'Try a different search term' : canCreate ? 'Click "Add Record" to create your first entry' : 'No records to display'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((row, i) => {
                  const rowKey  = row.id ?? row._id ?? i;
                  const isOpen  = isExpandable && expandedId === rowKey;
                  return (
                    <React.Fragment key={rowKey}>
                      <TableRow
                        sx={{
                          bgcolor: isOpen ? '#F0FDF4' : 'white',
                          borderBottom: isOpen ? 'none' : '1px solid #F3F4F6',
                          transition: 'background 0.1s',
                          '&:hover': { bgcolor: isOpen ? '#F0FDF4' : '#F9FAFB' },
                          cursor: 'default',
                        }}
                      >
                        {/* Checkbox column (if ELEMENT_TYPE or SALARY_TEMPLATE) */}
                        {['ELEMENT_TYPE', 'SALARY_TEMPLATE'].includes(tableName) && (
                          <TableCell padding="checkbox" sx={{ borderColor: '#F3F4F6' }}>
                            <Checkbox
                              color="primary"
                              checked={selectedIds.includes(rowKey)}
                              onChange={() => handleSelectRow(rowKey)}
                              size="small"
                            />
                          </TableCell>
                        )}

                        {/* SL number */}
                        <TableCell sx={{
                          textAlign: 'center', fontSize: 12.5, fontWeight: 500,
                          color: '#9CA3AF', py: 1.4, borderColor: '#F3F4F6',
                        }}>
                          {String(startRow + i).padStart(2, '0')}
                        </TableCell>

                        {visibleCols.map(col => (
                          <TableCell key={col} align={['sort_order', 'esi_ip_number', 'esi_estab_code'].includes(col) ? 'center' : 'left'} sx={{
                            py: 1.4, maxWidth: 220, overflow: 'hidden',
                            textOverflow: 'ellipsis', borderColor: '#F3F4F6',
                          }}>
                            {formatCell(row[col], col, row)}
                          </TableCell>
                        ))}

                        {/* Actions — always visible */}
                        <TableCell align="center" sx={{ py: 1, borderColor: '#F3F4F6' }}>
                          <Stack direction="row" justifyContent="center" spacing={0.5}>
                            {isCompanyTable && onManagePayroll && (() => {
                              const ready = !!(row.default_bg_id && row.default_ldg_id);
                              const isActive = activeCompanyId === row.company_id;
                              const tip = isActive ? 'This company is the active payroll context'
                                : ready ? 'Set as active payroll context'
                                : 'Set Default Business Group and Legislation Group first';
                              return (
                                <Tooltip title={tip} arrow placement="top">
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={!ready}
                                      onClick={() => onManagePayroll(row)}
                                      sx={{
                                        height: 28, px: 1.2, borderRadius: '7px', minWidth: 0,
                                        color: isActive ? 'white' : ready ? '#16a34a' : '#94A3B8',
                                        border: '1.5px solid',
                                        borderColor: isActive ? '#16a34a' : ready ? '#bbf7d0' : '#E5E7EB',
                                        bgcolor:    isActive ? '#16a34a' : ready ? '#f0fdf4' : '#F9FAFB',
                                        '&:hover': { bgcolor: isActive ? '#16a34a' : '#dcfce7', borderColor: '#22c55e' },
                                        '&.Mui-disabled': { color: '#D1D5DB', bgcolor: '#FAFBFC' },
                                      }}
                                    >
                                      <Typography fontSize={10.5} fontWeight={700} sx={{ letterSpacing: 0.3 }}>
                                        {isActive ? '✓ ACTIVE' : 'MANAGE PAYROLL'}
                                      </Typography>
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              );
                            })()}
                            {customRowAction && customRowAction(row)}
                            {onView && (
                              <Tooltip title="View" arrow placement="top">
                                <IconButton
                                  size="small"
                                  onClick={() => onView(row)}
                                  sx={{
                                    width: 28, height: 28, borderRadius: '7px',
                                    color: '#2563eb', border: '1.5px solid #E5E7EB', bgcolor: 'white',
                                    '&:hover': { bgcolor: '#eff6ff', borderColor: '#2563eb' },
                                  }}
                                >
                                  <VisibilityIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                              {onEdit && (() => {
                               const editDisabled = false;
                               return (
                                 <Tooltip title="Edit" arrow placement="top">
                                   <span>
                                     <IconButton
                                       size="small"
                                       disabled={editDisabled}
                                       onClick={() => onEdit(row)}
                                       sx={{
                                         width: 28, height: 28, borderRadius: '7px',
                                         color: editDisabled ? '#9CA3AF' : '#22c55e',
                                         border: '1.5px solid #E5E7EB',
                                         bgcolor: editDisabled ? '#F3F4F6' : 'white',
                                         '&:hover': !editDisabled && { bgcolor: '#f0fdf4', borderColor: '#22c55e' },
                                       }}
                                     >
                                       <EditIcon sx={{ fontSize: 13 }} />
                                     </IconButton>
                                   </span>
                                 </Tooltip>
                               );
                             })()}
                              {onDelete && (() => {
                               const deleteDisabled = false;
                               return (
                                 <Tooltip title="Delete" arrow placement="top">
                                   <span>
                                     <IconButton
                                       size="small"
                                       disabled={deleteDisabled}
                                       onClick={() => onDelete(row)}
                                       sx={{
                                         width: 28, height: 28, borderRadius: '7px',
                                         color: deleteDisabled ? '#9CA3AF' : '#EF4444',
                                         border: '1.5px solid #E5E7EB',
                                         bgcolor: deleteDisabled ? '#F3F4F6' : 'white',
                                         '&:hover': !deleteDisabled && { bgcolor: '#FEF2F2', borderColor: '#EF4444' },
                                       }}
                                     >
                                       <DeleteIcon sx={{ fontSize: 13 }} />
                                     </IconButton>
                                   </span>
                                 </Tooltip>
                               );
                             })()}
                            {isExpandable && (
                              <Tooltip title={isOpen ? 'Hide details' : expandTooltip} arrow placement="top">
                                <IconButton
                                  size="small"
                                  onClick={() => setExpandedId(isOpen ? null : rowKey)}
                                  sx={{
                                    width: 28, height: 28, borderRadius: '7px',
                                    color: isOpen ? '#16a34a' : '#6B7280',
                                    border: '1.5px solid #E5E7EB',
                                    bgcolor: isOpen ? '#f0fdf4' : 'white',
                                    '&:hover': { bgcolor: '#f0fdf4', borderColor: '#22c55e', color: '#16a34a' },
                                  }}
                                >
                                  {isOpen
                                    ? <KeyboardArrowUpIcon sx={{ fontSize: 15 }} />
                                    : <KeyboardArrowDownIcon sx={{ fontSize: 15 }} />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>

                      {isExpandable && (
                        <TableRow>
                          <TableCell colSpan={visibleCols.length + 2}
                            sx={{ p: 0, borderColor: '#F3F4F6', bgcolor: '#FAFBFC' }}>
                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                              {isCompanyTable && <CompanyStatutoryPanel companyId={row.company_id} />}
                              {isPersonTable  && <PersonOnboardingPanel  personId={row.person_id} />}
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Footer / Pagination ── */}
        <Box sx={{
          borderTop: '1.5px solid #F3F4F6', bgcolor: '#F9FAFB',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, flexWrap: 'wrap',
        }}>
          {filtered.length > 0 && (
            <Typography fontSize={12} color="#9CA3AF" sx={{ py: 1 }}>
              Showing{' '}
              <span style={{ color: '#374151', fontWeight: 600 }}>{startRow}–{endRow}</span>
              {' '}of{' '}
              <span style={{ color: '#374151', fontWeight: 600 }}>{filtered.length}</span>
              {search ? ' filtered' : ''} records
            </Typography>
          )}
          <TablePagination
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            component="div"
            count={filtered.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={e => { setRowsPerPage(+e.target.value); setPage(0); }}
            sx={{
              border: 'none',
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
                { fontSize: 12, color: '#6B7280' },
              '& .MuiTablePagination-select': { fontSize: 12 },
              '& .MuiTablePagination-actions button': {
                borderRadius: '7px', color: '#6B7280', border: '1.5px solid #E5E7EB',
                '&:hover': { bgcolor: '#f0fdf4', color: '#22c55e', borderColor: '#22c55e' },
              },
            }}
          />
        </Box>
      </Paper>

      {/* Bulk Delete Confirmation Dialog */}
      <ConfirmDialog
        open={bulkDeleteDialogOpen}
        title="Confirm Delete"
        message={`Are you sure you want to delete the selected ${tableName === 'ELEMENT_TYPE' ? 'Salary Structure (Components)' : 'records'}? This action cannot be undone.`}
        confirmLabel={isDeleting ? "Deleting..." : "Delete"}
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkDeleteDialogOpen(false)}
        confirmColor="error"
      />
    </Box>
  );
}
